import { useState, useCallback, useRef, useMemo } from "react";
import { FiTrendingUp, FiCopy } from "react-icons/fi";
import { cn } from "utils/helpers";
import Modal from "newui/components/ui/Modal/Modal";
import { Button } from "newui/components/Button";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { AutoSaveIndicator } from "newui/components/ui/AutoSave/AutoSaveIndicator";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel";
import type { SelectOption } from "newui/components/ui/InputWithLabel";
import { useAutoSave } from "newui/hooks/useAutoSave";
import { ScoringModalHeader } from "../InitialEvaluationStep/ScoringModalHeader";
import { FinalCriterionScoringRow } from "./FinalCriterionScoringRow";
import { ClarificationPane } from "./ClarificationPane";
import { RevisionPane } from "./RevisionPane";
import type {
	FinalCriterionScore,
	FinalScoringDetail,
	SupplierEvaluationSummary,
	UnsavedChangesAction,
	SupplierClarificationItem,
	RevisionFile,
} from "../../../types/scoring.type";

interface FinalScoringModalProps {
	isOpen: boolean;
	rfxId: number;
	selectedInviteId: number;
	suppliers: SupplierEvaluationSummary[];
	scoringDetail: FinalScoringDetail | null;
	clarifications: SupplierClarificationItem[];
	revisionFiles: RevisionFile[];
	isLoading?: boolean;
	readOnly?: boolean;
	maxScore?: number;
	onClose: () => void;
	onScoringComplete: () => void;
	onSupplierChange: (inviteId: number) => void;
	onSaveCriteria: (dirtyIds: number[], criteria: FinalCriterionScore[]) => Promise<boolean>;
}

export const FinalScoringModal: React.FC<FinalScoringModalProps> = ({
	isOpen,
	rfxId,
	suppliers,
	selectedInviteId,
	scoringDetail,
	clarifications,
	revisionFiles,
	isLoading = false,
	readOnly = false,
	maxScore = 10,
	onClose,
	onScoringComplete,
	onSupplierChange,
	onSaveCriteria,
}) => {
	const [criteria, setCriteria] = useState<FinalCriterionScore[]>(
		scoringDetail?.criteria ?? []
	);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const dirtyRef = useRef<Set<number>>(new Set());
	const pendingAction = useRef<(() => void) | null>(null);
	const changeTokenRef = useRef(0);
	const [changeToken, setChangeToken] = useState(0);
	const isSubmitted = scoringDetail?.isSubmitted ?? false;
	const effectiveReadOnly = readOnly || isSubmitted;

	const prevDetailRef = useRef<FinalScoringDetail | null>(null);
	if (scoringDetail !== prevDetailRef.current) {
		prevDetailRef.current = scoringDetail;
		setCriteria(scoringDetail?.criteria ?? []);
		dirtyRef.current = new Set();
	}

	const supplierOptions = useMemo(() => {
		return suppliers.map((s) => ({
			value: s.inviteId,
			label: s.supplierName,
		}));
	}, [suppliers]);

	const isDirty = dirtyRef.current.size > 0;

	const percentComplete = useMemo(() => {
		if (criteria.length === 0) {
			return 0;
		}
		const scored = criteria.filter((c) => c.finalScore !== null).length;
		return Math.round((scored / criteria.length) * 100);
	}, [criteria]);

	const saveDirtyFields = useCallback(async (): Promise<boolean> => {
		const dirtyIds = Array.from(dirtyRef.current);
		if (dirtyIds.length === 0) {
			return true;
		}
		setSaveError(null);
		const success = await onSaveCriteria(dirtyIds, criteria);
		if (success) {
			dirtyRef.current = new Set();
			onScoringComplete();
			return true;
		}
		setSaveError("Failed to save some changes");
		return false;
	}, [criteria, onSaveCriteria, onScoringComplete]);

	const autoSaveHandler = useCallback(async (): Promise<false | void> => {
		const success = await saveDirtyFields();
		if (!success) {
			return false;
		}
	}, [saveDirtyFields]);

	const { isSaving, lastSavedAt } = useAutoSave({
		isDirty,
		onSave: autoSaveHandler,
		changeToken,
		debounceMs: 2500,
	});

	const markDirty = useCallback((rfxCriteriaId: number) => {
		dirtyRef.current.add(rfxCriteriaId);
		changeTokenRef.current += 1;
		setChangeToken(changeTokenRef.current);
	}, []);

	const handleFinalScoreChange = useCallback(
		(rfxCriteriaId: number, score: number | null) => {
			setCriteria((prev) =>
				prev.map((c) =>
					c.rfxCriteriaId === rfxCriteriaId
						? { ...c, finalScore: score }
						: c
				)
			);
			markDirty(rfxCriteriaId);
		},
		[markDirty]
	);

	const handleFinalCommentChange = useCallback(
		(rfxCriteriaId: number, comment: string | null) => {
			setCriteria((prev) =>
				prev.map((c) =>
					c.rfxCriteriaId === rfxCriteriaId
						? { ...c, finalComment: comment }
						: c
				)
			);
			markDirty(rfxCriteriaId);
		},
		[markDirty]
	);

	const allScoresFilled = criteria.length > 0 && criteria.every((c) => c.finalScore !== null);

	const handleKeepAllOriginalScores = useCallback(() => {
		setCriteria((prev) =>
			prev.map((c) => {
				if (c.finalScore === null && c.initialScore !== null) {
					dirtyRef.current.add(c.rfxCriteriaId);
					return { ...c, finalScore: c.initialScore };
				}
				return c;
			})
		);
		changeTokenRef.current += 1;
		setChangeToken(changeTokenRef.current);
	}, []);

	const handleClearAllFinalScores = useCallback(() => {
		setCriteria((prev) =>
			prev.map((c) => {
				if (c.finalScore !== null) {
					dirtyRef.current.add(c.rfxCriteriaId);
					return { ...c, finalScore: null };
				}
				return c;
			})
		);
		changeTokenRef.current += 1;
		setChangeToken(changeTokenRef.current);
	}, []);

	const handleSupplierChange = useCallback(
		(invite: SelectOption) => {
			const inviteId = invite.value;
			if (isDirty) {
				pendingAction.current = () => {
					onSupplierChange(inviteId);
				};
				setShowConfirmDialog(true);
				return;
			}
			onSupplierChange(inviteId);
		},
		[isDirty, onSupplierChange]
	);

	const handleClose = useCallback(() => {
		if (isDirty) {
			pendingAction.current = () => {
				onScoringComplete();
				onClose();
			};
			setShowConfirmDialog(true);
			return;
		}
		onScoringComplete();
		onClose();
	}, [isDirty, onClose, onScoringComplete]);

	const handleConfirmAction = useCallback(
		async (action: UnsavedChangesAction) => {
			setShowConfirmDialog(false);
			if (action === "cancel") {
				pendingAction.current = null;
				return;
			}
			if (action === "save") {
				const saved = await saveDirtyFields();
				if (!saved) {
					return;
				}
			}
			if (pendingAction.current) {
				pendingAction.current();
				pendingAction.current = null;
			}
		},
		[saveDirtyFields]
	);

	const hasEmptyScoresWithInitial = criteria.some(
		(c) => c.finalScore === null && c.initialScore !== null
	);

	if (!isOpen) {
		return null;
	}

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={handleClose}
				title="Final Score Supplier"
				titleColor="blue.800"
				customIcon={<FiTrendingUp size={30} className="fill-white stroke-blue-700" />}
				description={
					<div className="ml-4 md:ml-8 flex flex-wrap items-center gap-2 md:gap-4">
						<SelectWithLabel
							className="mt-3"
							value={selectedInviteId}
							options={supplierOptions}
							onChange={handleSupplierChange}
							data-testid="final-supplier-select"
						/>
						<AutoSaveIndicator
							isSaving={isSaving}
							lastSavedAt={lastSavedAt}
							error={saveError}
							size="xs"
						/>
					</div>
				}
				wrapDescription
				size="full"
				closeOnEsc={!isDirty}
				closeOnOverlayClick={false}
				customFooter={
					<div className="flex items-center justify-end w-full px-4">
						<Button
							btntype="primary"
							size="xs"
							onClick={handleClose}
							data-testid="final-scoring-modal-close-btn"
						>
							Save and Close
						</Button>
					</div>
				}
			>
				<div
					className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-0 h-[calc(100vh-200px)]"
					data-testid="final-scoring-modal-content"
				>
					{/* Left pane: Clarifications & Revisions */}
					<div
						className="overflow-y-auto border-r border-gray-200 p-4 space-y-6"
						data-testid="final-scoring-left-pane"
					>
						<ClarificationPane clarifications={clarifications} rfxId={rfxId} />
						<RevisionPane files={revisionFiles} />
					</div>

					{/* Right pane: Final Scoring */}
					<div
						className="overflow-y-auto p-4 space-y-4"
						data-testid="final-scoring-right-pane"
					>
						<ScoringModalHeader
							suppliers={suppliers}
							selectedInviteId={selectedInviteId}
							percentComplete={percentComplete}
							rfxId={rfxId}
							readOnly={effectiveReadOnly}
						/>

						{!effectiveReadOnly && (hasEmptyScoresWithInitial || allScoresFilled) && (
							<div className="flex justify-end">
								{allScoresFilled ? (
									<button
										type="button"
										onClick={handleClearAllFinalScores}
										className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors"
										data-testid="clear-all-final-btn"
									>
										Clear final scores
									</button>
								) : (
									<button
										type="button"
										onClick={handleKeepAllOriginalScores}
										className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors"
										data-testid="keep-all-original-btn"
									>
										<FiCopy size={12} />
										Keep all original scores
									</button>
								)}
							</div>
						)}

						{isLoading && (
							<div className="py-8 text-sm text-gray-400" data-testid="final-scoring-loading">
								<LoadingSpinner label="Loading final scores" />
							</div>
						)}

						{!isLoading && criteria.length > 0 && (
							<div className="space-y-4" data-testid="final-scoring-grid">
								{criteria.map((criterion) => (
									<FinalCriterionScoringRow
										key={criterion.rfxCriteriaId}
										criterion={criterion}
										maxScore={maxScore}
										readOnly={effectiveReadOnly}
										onFinalScoreChange={handleFinalScoreChange}
										onFinalCommentChange={handleFinalCommentChange}
									/>
								))}
							</div>
						)}

						{!isLoading && criteria.length === 0 && (
							<div className="text-center py-8 text-sm text-gray-400" data-testid="final-scoring-empty">
								No scoring data available for this supplier.
							</div>
						)}
					</div>
				</div>
			</Modal>

			{showConfirmDialog && (
				<Modal
					isOpen={showConfirmDialog}
					onClose={() => handleConfirmAction("cancel")}
					title="Unsaved Changes"
					size="md"
					iconType="warning"
					modalType="alert"
					closeOnEsc
					closeOnOverlayClick
					customFooter={
						<div className="flex w-full justify-center gap-3">
							<button
								type="button"
								onClick={() => handleConfirmAction("cancel")}
								className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={() => handleConfirmAction("save")}
								className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
							>
								Save &amp; Continue
							</button>
						</div>
					}
				>
					<p className="text-base text-gray-600 text-center">
						You have unsaved scoring changes.
						<br /><br />
						Would you like to save them before continuing?
					</p>
				</Modal>
			)}
		</>
	);
};
